/**
 * Chat API Slice (RTK Query)
 * Polling-based chat for analysis report messages.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { apiClient } from '@/auth';

//  Types 

export interface ChatMessage {
    id: number;
    role: 'user' | 'assistant';
    content: string;
    created_at: string;
}

export interface ChatHistoryResponse {
    messages: ChatMessage[];
    total: number;
}

export interface SendMessageResponse {
    success: boolean;
    message: string;
    hint: string;
}

export interface SuggestedQuestionsResponse {
    success: boolean;
    data: {
        analysis_id: number;
        question_count: number;
        suggestions?: string[];
    } | null;
    error?: string | null;
}

export interface GetChatHistoryArg {
    analysisId: number;
}

export interface SendMessageArg {
    analysisId: number;
    keyword: string;
    message: string;
}

//  Base Query 

interface BaseQueryArgs {
    url: string;
    method?: 'GET' | 'POST';
    params?: Record<string, unknown>;
    body?: Record<string, unknown>;
}

const buildQueryString = (params: Record<string, unknown>): string => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
        }
    });
    return searchParams.toString();
};

const apiClientBaseQuery = async ({ url, method = 'GET', params, body }: BaseQueryArgs) => {
    try {
        if (method === 'POST') {
            const urlWithParams = params
                ? `${url}?${buildQueryString(params)}`
                : url;
            const response = await apiClient.post(urlWithParams, body ?? {}, { retries: false });
            return { data: response.data };
        }

        const response = await apiClient.get(url, params as Record<string, unknown>);
        return { data: response.data };
    } catch (error: unknown) {
        const apiError = error as { response?: { status?: number; data?: unknown }; message?: string };
        return {
            error: {
                status: apiError.response?.status ?? 500,
                data: apiError.response?.data ?? apiError.message,
            },
        };
    }
};

//  API 

export const CHAT_POLLING_INTERVAL = 10_000;

export const chatApi = createApi({
    reducerPath: 'chatApi',
    baseQuery: apiClientBaseQuery,
    endpoints: (builder) => ({
        /**
         * GET /reports/{analysis_id}/chat?keyword=...
         * Polled every 10 seconds to fetch the updated message list.
         */
        getChatHistory: builder.query<ChatHistoryResponse, GetChatHistoryArg>({
            query: ({ analysisId }) => ({
                url: `reports/${analysisId}/chat`,
            }),
        }),

        /**
         * POST /reports/{analysis_id}/chat?keyword=...
         * Body: { message }
         */
        sendMessage: builder.mutation<SendMessageResponse, SendMessageArg>({
            query: ({ analysisId, keyword, message }) => ({
                url: `reports/${analysisId}/chat`,
                method: 'POST',
                params: { keyword },
                body: { message },
            }),
        }),

        /**
         * POST /reports/{analysis_id}/chat/suggest-questions
         * Generates clickable follow-up questions based on the chat history.
         */
        getSuggestedQuestions: builder.query<SuggestedQuestionsResponse, GetChatHistoryArg>({
            query: ({ analysisId }) => ({
                url: `reports/${analysisId}/chat/suggest-questions`,
                method: 'POST',
            }),
        }),
    }),
});

export const {
    useGetChatHistoryQuery,
    useSendMessageMutation,
    useGetSuggestedQuestionsQuery,
} = chatApi;
